import { useAtom, useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  appendNineSegDriverTop5Block,
  appendNineSegSegmentTop5Block,
  appendSampleThresholdBlock,
  filterVisibleAnswersArrayData,
  filterVisibleCustomerAttributesArrayData,
  isImageQuestionText,
  type NineSegmentDisplayColumnKey,
  type NineSegmentSortColumnKey,
  nineSegmentThresholdInputs,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import {
  AnalysisCommentBlock,
  useGenerateSingleComment,
} from '~/components/features/AnalysisCommentBlock'
import { DisplayToggleButton } from '~/components/features/DisplayToggleButton'
import { ShareDropDownMenuButton } from '~/components/features/ShareDropDownMenuButton'
import {
  SortButton,
  type SortButtonProps,
} from '~/components/features/SortButton'
import { Table } from '~/components/features/Table'
import { TableContainer } from '~/components/features/TableContainer'
import { AmplitudeProvider } from '~/components/providers/AmplitudeEventProvider'
import { useKeyedAtomEntry } from '~/hooks/jotai'
import { useLogEvent } from '~/hooks/useLogEvent'
import {
  type Brand,
  useSurveyQuerySurveyFilterTextGroups,
} from '~/hooks/useSurveyQuery'
import {
  type DisplayColumnItem,
  exists,
  formatFilterTextGroups,
  formatNineSegment,
  reversedNineSegments,
  serializeTableToText,
} from '~/utils'

import {
  dataDisplayTypeAtom,
  hiddenRowIdsAtom,
  nineSegmentCommentAtom,
  nineSegmentHiddenColumnKeysAtom,
  nineSegmentSortValueAtom,
  savedNineSegmentCommentAtom,
} from '../atoms'
import { useRestoringParams } from '../hooks/restoringParams/useRestoringParams'
import { useDisplayRowItem } from '../hooks/useDisplayRowItem'
import { useExpandText } from '../hooks/useExpandText'
import { useFigureInfo } from '../hooks/useFigureInfo'
import { useNineSegmentsAnswersArrayData } from '../hooks/useNineSegmentsAnswersArrayData'
import { useNineSegmentsCustomerAttributesArrayData } from '../hooks/useNineSegmentsCustomerAttributesArrayData'
import { useNineSegmentsTableColumns } from '../hooks/useNineSegmentsTableColumns'
import { useNineSegmentsTableInfo } from '../hooks/useNineSegmentsTableInfo'
import { useNineSegmentsTotalData } from '../hooks/useNineSegmentsTotalData'
import { useSurveyId } from '../hooks/useSurveyId'

export type NineSegmentsTableProps = {
  brand: Brand
  className?: string
}

// useLogEvent()はAmplitudeProviderの子孫でないと withSuffix が効かないため、
// Providerをレンダーする側とuseLogEvent()を呼ぶ側を分離する
export const NineSegmentsTable = (props: NineSegmentsTableProps) => (
  <AmplitudeProvider analysisName="customer data" eventNameSuffix="_9segs">
    <NineSegmentsTableInner {...props} />
  </AmplitudeProvider>
)

const NineSegmentsTableInner = ({
  brand,
  className,
}: NineSegmentsTableProps) => {
  const [comment, setComment] = useKeyedAtomEntry(
    nineSegmentCommentAtom,
    `${brand.id}`,
  )
  const [savedComment, setSavedComment] = useKeyedAtomEntry(
    savedNineSegmentCommentAtom,
    `${brand.id}`,
  )
  const { generate, isGenerating } = useGenerateSingleComment()
  const surveyId = useSurveyId()
  const { t } = useTranslation()
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const columns = useNineSegmentsTableColumns()
  const tableInfo = useNineSegmentsTableInfo(brand)
  const totalData = useNineSegmentsTotalData(brand)
  const answersArrayData = useNineSegmentsAnswersArrayData(brand)
  const customerAttributesArrayData =
    useNineSegmentsCustomerAttributesArrayData(brand)

  const { handleTextExpandedChange, isTextExpanded, textLineCount } =
    useExpandText(tableInfo)

  const figureInfo = useFigureInfo(brand)
  const filterTextGroups = useSurveyQuerySurveyFilterTextGroups()
  const restoringParams = useRestoringParams(`${brand.id}`)
  const image = (
    <Table
      columns={columns}
      tableInfo={tableInfo}
      textLineCount={textLineCount}
    />
  )

  const [sortValue, setSortValue] = useAtom(nineSegmentSortValueAtom)

  const displayRowItems = useDisplayRowItem(brand, '9segs')
  const [hiddenRowIds, setHiddenRowIds] = useAtom(hiddenRowIdsAtom)
  // 表テキストは useNineSegmentsTableInfo が非表示行を除いた結果をシリアライズする。
  // Top5 ブロックも同じ可視行から作らないと、非表示にした項目がブロックにだけ残り、
  // 「表から選び直さない」契約の LLM がそれを書き写してしまう。
  const visibleAnswersArrayData = React.useMemo(
    () => filterVisibleAnswersArrayData(answersArrayData, hiddenRowIds),
    [answersArrayData, hiddenRowIds],
  )
  const visibleCustomerAttributesArrayData = React.useMemo(
    () =>
      filterVisibleCustomerAttributesArrayData(
        customerAttributesArrayData,
        hiddenRowIds,
      ),
    [customerAttributesArrayData, hiddenRowIds],
  )

  const [hiddenColumnKeys, setHiddenColumnKeys] = useAtom(
    nineSegmentHiddenColumnKeysAtom,
  )
  const displayColumnItems = React.useMemo<
    DisplayColumnItem<NineSegmentDisplayColumnKey>[]
  >(
    () => [
      {
        columnKey: 'total',
        isVisible: !hiddenColumnKeys.includes('total'),
        label: t('overall'),
      },
      ...reversedNineSegments.map((nineSegment) => ({
        columnKey: nineSegment,
        isVisible: !hiddenColumnKeys.includes(nineSegment),
        label: formatNineSegment(nineSegment, '%t1', t),
      })),
      {
        columnKey: 'totalDiff',
        isVisible: !hiddenColumnKeys.includes('totalDiff'),
        label: t('passiveoverallactiveoverall'),
      },
      {
        columnKey: 'currentUserDiff',
        isVisible: !hiddenColumnKeys.includes('currentUserDiff'),
        label: t('userpassiveuseractive'),
      },
      {
        columnKey: 'recognizedDiff',
        isVisible: !hiddenColumnKeys.includes('recognizedDiff'),
        label: t('passiveaware_noncustomersactiveaware_noncustomers'),
      },
      {
        columnKey: 'estrangedDiff',
        isVisible: !hiddenColumnKeys.includes('estrangedDiff'),
        label: t('passivedefected_customersactivedefected_customers'),
      },
      {
        columnKey: 'generalDiff',
        isVisible: !hiddenColumnKeys.includes('generalDiff'),
        label: t('passiveregular_customersactiveregular_customers'),
      },
      {
        columnKey: 'loyalDiff',
        isVisible: !hiddenColumnKeys.includes('loyalDiff'),
        label: t('passiveloyal_customersactiveloyal_customers'),
      },
      {
        columnKey: 'recognizedPositiveGeneralPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'recognizedPositiveGeneralPositiveDiff',
        ),
        label: t('activeaware_noncustomersactiveregular_customers'),
      },
      {
        columnKey: 'estrangedPositiveGeneralPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'estrangedPositiveGeneralPositiveDiff',
        ),
        label: t('activedefected_customersactiveregular_customers'),
      },
      {
        columnKey: 'generalPositiveLoyalPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'generalPositiveLoyalPositiveDiff',
        ),
        label: t('activeregular_customersactiveloyal_customers'),
      },
      {
        columnKey: 'recognizedNegativeGeneralPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'recognizedNegativeGeneralPositiveDiff',
        ),
        label: t('passiveaware_noncustomersactiveregular_customers'),
      },
      {
        columnKey: 'estrangedNegativeGeneralPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'estrangedNegativeGeneralPositiveDiff',
        ),
        label: t('passivedefected_customersactiveregular_customers'),
      },
      {
        columnKey: 'generalNegativeLoyalPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'generalNegativeLoyalPositiveDiff',
        ),
        label: t('passiveregular_customersactiveloyal_customers'),
      },
      {
        columnKey: 'recognizedNegativeLoyalPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'recognizedNegativeLoyalPositiveDiff',
        ),
        label: t('passiveaware_noncustomersactiveloyal_customers'),
      },
      {
        columnKey: 'estrangedNegativeLoyalPositiveDiff',
        isVisible: !hiddenColumnKeys.includes(
          'estrangedNegativeLoyalPositiveDiff',
        ),
        label: t('passivedefected_customersactiveloyal_customers'),
      },
    ],
    [hiddenColumnKeys, t],
  )

  const sortButtonItems: SortButtonProps<NineSegmentSortColumnKey>['items'] =
    React.useMemo(
      () => [
        { columnKey: 'total', label: t('overall') },
        ...reversedNineSegments.map((nineSegment) => ({
          columnKey: nineSegment,
          label: formatNineSegment(nineSegment, '%t1', t),
        })),
        { columnKey: 'totalNegative', label: t('passive_overall') },
        { columnKey: 'totalPositive', label: t('active_overall') },
        {
          columnKey: 'totalDiff',
          label: t('active_overall_vs_passive_overall'),
        },
        {
          columnKey: 'currentUserNegative',
          label: t('passive_existing_customer'),
        },
        {
          columnKey: 'currentUserPositive',
          label: t('active_existing_customer'),
        },
        {
          columnKey: 'currentUserDiff',
          label: t('active_existing_customer_vs_passive_existing_customer'),
        },
        {
          columnKey: 'recognizedDiff',
          label: t('active_aware_noncustomers_vs_passive_noncustomers'),
        },
        {
          columnKey: 'estrangedDiff',
          label: t('active_defected_customers_vs_passive_defected_customers'),
        },
        {
          columnKey: 'generalDiff',
          label: t('active_regular_customers_vs_passive_regular_customers'),
        },
        {
          columnKey: 'loyalDiff',
          label: t('active_loyal_customers_vs_passive_loyal_customers'),
        },
        {
          columnKey: 'recognizedPositiveGeneralPositiveDiff',
          label: t('active_aware_noncustomers_vs_active_regular_customers'),
        },
        {
          columnKey: 'estrangedPositiveGeneralPositiveDiff',
          label: t('active_defected_customers_vs_active_regular_customers'),
        },
        {
          columnKey: 'generalPositiveLoyalPositiveDiff',
          label: t('active_regular_customers_vs_active_loyal_customers'),
        },
        {
          columnKey: 'recognizedNegativeGeneralPositiveDiff',
          label: t(
            'differencepassiveaware_noncustomersactiveregular_customers',
          ),
        },
        {
          columnKey: 'estrangedNegativeGeneralPositiveDiff',
          label: t(
            'differencepassivedefected_customersactiveregular_customers',
          ),
        },
        {
          columnKey: 'generalNegativeLoyalPositiveDiff',
          label: t('differencepassiveregular_customersactiveloyal_customers'),
        },
        {
          columnKey: 'recognizedNegativeLoyalPositiveDiff',
          label: t('differencepassiveaware_noncustomersactiveloyal_customers'),
        },
        {
          columnKey: 'estrangedNegativeLoyalPositiveDiff',
          label: t('differencepassivedefected_customersactiveloyal_customers'),
        },
      ],
      [t],
    )

  const logEvent = useLogEvent()
  const handleAutoGenerate = React.useCallback(async () => {
    const result = await generate({
      brandName: brand.name,
      filterDescription:
        filterTextGroups.length === 0
          ? ''
          : formatFilterTextGroups(filterTextGroups),
      filterPatternIndex: 0,
      outputType: 'standardAnalysis',
      segmentMode: 'nineSegs',
      surveyId,
      // Only emit the threshold block when cells are percentages; the analyzer's sample-size guard compares item %, so non-% display modes would mismatch. Absent block ⇒ analyzer says nothing about sample size (by contract).
      // The driver-Top5 block is appended in EVERY display mode: it is computed from
      // ratios (not from the displayed cells), and the prompt's pts wording matches it.
      // セグメント別%Top5ブロックは全設問に付ける (場合分けAの選択・読み飛ばし対策。
      // ゲート不要 — %の事実列挙で、場合分けBへの誤誘導性が無い)。
      tabulationData: appendNineSegSegmentTop5Block(
        appendNineSegDriverTop5Block(
          dataDisplayType === 'percentage'
            ? appendSampleThresholdBlock(
                serializeTableToText(columns, tableInfo),
                nineSegmentThresholdInputs(totalData.counts, t),
              )
            : serializeTableToText(columns, tableInfo),
          // この画面には設問種別のメタデータが無いので、設問文でゲートする
          // (通常設問にブロックを付けると場合分けBへ誤誘導される — appendDriverTop5Block.ts 参照)。
          visibleAnswersArrayData.filter(
            (rows) =>
              0 < rows.length &&
              isImageQuestionText(rows[0].question.question_text),
          ),
          t,
          hiddenColumnKeys,
        ),
        visibleAnswersArrayData,
        t,
        hiddenColumnKeys,
        // 顧客属性の行 (ライフステージ等) も属性グループごとの節として載せる。
        visibleCustomerAttributesArrayData,
      ),
    })
    if (exists(result)) {
      setComment(result)
      logEvent('Generate AI comment', { withSuffix: true })
    }
  }, [
    generate,
    logEvent,
    brand.name,
    filterTextGroups,
    surveyId,
    dataDisplayType,
    columns,
    tableInfo,
    totalData,
    visibleAnswersArrayData,
    visibleCustomerAttributesArrayData,
    hiddenColumnKeys,
    t,
    setComment,
  ])

  return (
    <>
      <AnalysisCommentBlock
        isGenerating={isGenerating}
        savedComment={savedComment}
        saveDocumentImage={{
          commentReadOnly: true,
          figureInfo,
          image,
          initialComment: comment,
          onSaved: (saved) => setSavedComment(saved),
          restoringParams,
        }}
        value={comment}
        onAutoGenerate={handleAutoGenerate}
        onCommentChange={setComment}
      />
      <TableContainer
        className={className}
        header={
          <>
            <SortButton
              items={sortButtonItems}
              value={sortValue}
              onChange={setSortValue}
            />

            <DisplayToggleButton
              columnItems={displayColumnItems}
              isTextExpanded={isTextExpanded}
              isVisibleExpandTextCheckbox={true}
              rowItems={displayRowItems}
              onHiddenColumnChange={setHiddenColumnKeys}
              onHiddenRowChange={setHiddenRowIds}
              onTextExpandedChange={handleTextExpandedChange}
            />

            <ShareDropDownMenuButton
              copyToClipboard={{
                columns,
                figureInfo,
                restoringParams,
                tableInfo,
              }}
              downloadAsPng={{ image }}
              getSharedUrl={{ restoringParams }}
              saveDocumentImage={{
                commentReadOnly: true,
                figureInfo,
                image,
                initialComment: comment,
                onSaved: (saved) => setSavedComment(saved),
                restoringParams,
              }}
            />
          </>
        }
      >
        <Table
          columns={columns}
          stickyColumns={3}
          tableInfo={tableInfo}
          textLineCount={textLineCount}
        />
      </TableContainer>
    </>
  )
}
